/**
 * Note-name -> frequency helper for the chiptune synth. Supports the usual
 * scientific pitch notation ('C4', 'F#3', 'Bb2', ...). '-' (rest) and '.'
 * (hold/sustain previous note) are handled by the pattern player, not here.
 */

const NOTE_OFFSETS: Record<string, number> = {
  C: 0,
  'C#': 1,
  Db: 1,
  D: 2,
  'D#': 3,
  Eb: 3,
  E: 4,
  F: 5,
  'F#': 6,
  Gb: 6,
  G: 7,
  'G#': 8,
  Ab: 8,
  A: 9,
  'A#': 10,
  Bb: 10,
  B: 11,
};

const NOTE_RE = /^([A-Ga-g])(#|b)?(-?\d+)$/;

/** Returns null for rests/holds/unparseable input. */
export function noteToFreq(note: string | undefined | null): number | null {
  if (!note || note === '-' || note === '.') return null;
  const m = NOTE_RE.exec(note.trim());
  if (!m) return null;
  const letter = m[1].toUpperCase();
  const accidental = m[2] ?? '';
  const octave = parseInt(m[3], 10);
  const key = letter + accidental;
  const offset = NOTE_OFFSETS[key];
  if (offset === undefined) return null;
  const midi = (octave + 1) * 12 + offset;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function isRest(token: string): boolean {
  return token === '-';
}

export function isHold(token: string): boolean {
  return token === '.';
}

/** Build one bar of `count` sixteenth-note steps from a space-separated
 * string, e.g. bar16('A2 . . . . . . . E3 . . . . . . .'). Throws if the
 * token count doesn't match `count`, to catch authoring typos early. */
export function bar(spec: string, count = 16): string[] {
  const toks = spec.trim().split(/\s+/);
  if (toks.length !== count) {
    throw new Error(`bar(): expected ${count} tokens, got ${toks.length}: "${spec}"`);
  }
  return toks;
}

export function restBar(count = 16): string[] {
  return new Array(count).fill('-');
}

/** Given a pattern array and a step index that holds a note, count how many
 * subsequent '.' hold tokens extend it, returning the total duration in
 * steps (minimum 1). */
export function noteDurationSteps(pattern: readonly string[], index: number): number {
  let n = 1;
  for (let i = index + 1; i < pattern.length; i++) {
    if (isHold(pattern[i])) n++;
    else break;
  }
  return n;
}
